import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  AttachFile as FileIcon,
  Delete as DeleteIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';

interface BoxUploaderProps {
  open: boolean;
  onClose: () => void;
  claimId: string;
}

interface FileUploadStatus {
  file: File;
  status: 'pending' | 'uploading' | 'success' | 'error';
  progress: number;
  error?: string;
}

const BoxUploader: React.FC<BoxUploaderProps> = ({ open, onClose, claimId }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [files, setFiles] = useState<FileUploadStatus[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);

  useEffect(() => {
    if (open) {
      console.log('📤 Box uploader opened for claim:', claimId);
      setIsLoading(false); // No need to fetch token on client side anymore
      setError('');
      setFiles([]);
    }
  }, [open, claimId]);

  const handleFileSelect = (selectedFiles: FileList | null) => {
    if (!selectedFiles) return;

    const newFiles: FileUploadStatus[] = Array.from(selectedFiles).map(file => ({
      file,
      status: 'pending',
      progress: 0,
    }));

    setFiles(prev => [...prev, ...newFiles]);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    handleFileSelect(e.dataTransfer.files);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = () => {
    setIsDragOver(false);
  };

  const uploadFile = async (fileStatus: FileUploadStatus, index: number) => {
    const { file } = fileStatus;

    setFiles(prev => prev.map((f, i) =>
      i === index ? { ...f, status: 'uploading', progress: 0 } : f
    ));

    try {
      const formData = new FormData();
      formData.append('file', file);

      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          const progress = Math.round((e.loaded / e.total) * 100);
          setFiles(prev => prev.map((f, i) =>
            i === index ? { ...f, progress } : f
          ));
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status === 200) {
          setFiles(prev => prev.map((f, i) =>
            i === index ? { ...f, status: 'success', progress: 100 } : f
          ));
          console.log(`✅ File uploaded successfully for claim ${claimId}:`, file.name);
        } else {
          const response = JSON.parse(xhr.responseText);
          throw new Error(response.error || `Upload failed: ${xhr.status}`);
        }
      });

      xhr.addEventListener('error', () => {
        setFiles(prev => prev.map((f, i) =>
          i === index ? { ...f, status: 'error', error: 'Network error' } : f
        ));
      });

      // Upload through our server endpoint instead of directly to Box
      xhr.open('POST', '/api/box/upload');
      xhr.send(formData);

    } catch (error) {
      console.error('Upload error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Upload failed';
      setFiles(prev => prev.map((f, i) =>
        i === index ? { ...f, status: 'error', error: errorMessage } : f
      ));
    }
  };

  const handleUpload = () => {
    files.forEach((file, index) => {
      if (file.status === 'pending') {
        uploadFile(file, index);
      }
    });
  };

  const removeFile = (index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const getStatusIcon = (status: FileUploadStatus['status']) => {
    switch (status) {
      case 'success':
        return <SuccessIcon color="success" />;
      case 'error':
        return <ErrorIcon color="error" />;
      case 'uploading':
        return <CircularProgress size={24} />;
      default:
        return <FileIcon />;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          minHeight: '500px',
        }
      }}
    >
      <DialogTitle>
        Upload Documents - Claim {claimId}
      </DialogTitle>
      <DialogContent>
        {isLoading && (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            minHeight="300px"
          >
            <CircularProgress />
            <Typography variant="body2" sx={{ mt: 2 }}>
              Loading uploader...
            </Typography>
          </Box>
        )}

        {error && (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            minHeight="300px"
          >
            <Typography color="error" gutterBottom>
              {error}
            </Typography>
            <Button
              variant="contained"
              onClick={() => {
                setError('');
                setIsLoading(false);
              }}
              sx={{ mt: 2 }}
            >
              Retry
            </Button>
          </Box>
        )}

        {!isLoading && !error && (
          <>
            {/* Drop Zone */}
            <Box
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              sx={{
                border: '2px dashed',
                borderColor: isDragOver ? 'primary.main' : 'grey.300',
                borderRadius: 2,
                p: 4,
                textAlign: 'center',
                cursor: 'pointer',
                bgcolor: isDragOver ? 'action.hover' : 'background.paper',
                mb: 2,
                transition: 'all 0.2s ease',
              }}
              onClick={() => fileInputRef.current?.click()}
            >
              <UploadIcon sx={{ fontSize: 48, color: 'grey.400', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                Drop files here or click to browse
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Supported formats: PDF, DOC, DOCX, JPG, PNG
              </Typography>
            </Box>

            <input
              ref={fileInputRef}
              type="file"
              multiple
              style={{ display: 'none' }}
              onChange={(e) => handleFileSelect(e.target.files)}
            />

            {/* File List */}
            {files.length > 0 && (
              <>
                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                  Files to Upload
                </Typography>
                <List>
                  {files.map((fileStatus, index) => (
                    <ListItem key={index} divider>
                      <ListItemIcon>
                        {getStatusIcon(fileStatus.status)}
                      </ListItemIcon>
                      <ListItemText
                        primary={fileStatus.file.name}
                        secondary={
                          <>
                            {Math.round(fileStatus.file.size / 1024)} KB
                            {fileStatus.status === 'uploading' && (
                              <LinearProgress
                                variant="determinate"
                                value={fileStatus.progress}
                                sx={{ mt: 1 }}
                              />
                            )}
                            {fileStatus.error && (
                              <Typography color="error" variant="caption" display="block">
                                {fileStatus.error}
                              </Typography>
                            )}
                          </>
                        }
                      />
                      <IconButton
                        edge="end"
                        onClick={() => removeFile(index)}
                        disabled={fileStatus.status === 'uploading'}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </ListItem>
                  ))}
                </List>
              </>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        {files.length > 0 && files.some(f => f.status === 'pending') && (
          <Button
            variant="contained"
            onClick={handleUpload}
          >
            Upload Files
          </Button>
        )}
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default BoxUploader;